import { useEffect, useRef, lazy, Suspense } from "react"
import { BrowserRouter, Routes, Route, useSearchParams } from "react-router-dom"
import { DatabaseProvider } from "./providers/DatabaseProvider"
import { CurrentUserProvider } from "@/shared/contexts"
import { AppLayout } from "./layout/AppLayout"
import { AppRoutes } from "./routes"
import { patchWindowScrollForMacOS } from "@/features/dnd/macOSScrollFix"
// S15: Heavy modals lazy-loaded — chunks fetched when first opened
const LazySearchModal = lazy(() =>
  import("@/features/search/SearchModal").then((m) => ({ default: m.SearchModal })),
)
const LazySettingsModal = lazy(() =>
  import("@/features/settings/SettingsModal").then((m) => ({ default: m.SettingsModal })),
)
const LazyWelcomeDialog = lazy(() =>
  import("@/features/onboarding").then((m) => ({ default: m.WelcomeDialog })),
)
import { Toaster } from "@/app/components/ui/sonner"
import { FloatingDoneButton } from "@/app/components/FloatingDoneButton"

import { DevToolsWidget } from "@/app/components/DevToolsWidget"
import { LogoutPage } from "@/features/auth/pages/LogoutPage"
import { LoginErrorPage } from "@/features/auth/pages/LoginErrorPage"
import { ErrorBoundary } from "@/app/components/ErrorBoundary"
import { OfflineIndicator } from "@/app/components/OfflineIndicator"
import { PrivacyPage } from "@/features/legal/PrivacyPage"
import { TermsPage } from "@/features/legal/TermsPage"
import { ServerErrorPage } from "@/features/error/ServerErrorPage"
import { OtpRedirectPage } from "@/features/auth/pages/OtpRedirectPage"
import { GoMarketingPage } from "@/features/marketing/go/GoMarketingPage"
import { DownloadMarketingPage } from "@/features/marketing/download/DownloadMarketingPage"
import { AffiliateMarketingPage } from "@/features/marketing/affiliate/AffiliateMarketingPage"
import { SupportMarketingPage } from "@/features/marketing/support/SupportMarketingPage"
import { GuestSharePage } from "@/features/sharing/GuestSharePage"
import { OAuthConsentPage } from "@/features/oauth/OAuthConsentPage"
import { OAuthConsentResume, PreAuthOAuthConsentCapture } from "@/features/oauth/OAuthConsentSession"
import { captureAffiliate, type TrackingData } from "@/shared/utils/affiliate/fingerprint"

const PRE_AUTH_TRACKING_KEYS = [
  "affiliate",
  "utm_source",
  "utm_content",
  "utm_medium",
  "utm_term",
  "utm_campaign",
  "fbclid",
  "gclid",
] as const

/**
 * Captures affiliate/UTM tracking params to localStorage BEFORE the auth gate.
 * This ensures params from URLs like `totodo.app/?ref=partner123` survive
 * the login redirect (Dexie Cloud auth may change the URL).
 *
 * Renders nothing — pure side-effect component.
 */
function PreAuthTrackingCapture() {
  const [searchParams] = useSearchParams()
  const hasCaptured = useRef(false)

  useEffect(() => {
    if (hasCaptured.current) return

    const trackingData: TrackingData = {}
    let hasAnyParam = false

    // "ref" is a marketing-friendly alias for "affiliate"
    const ref = searchParams.get("ref")
    if (ref) {
      trackingData.affiliate = ref
      hasAnyParam = true
    }

    for (const key of PRE_AUTH_TRACKING_KEYS) {
      const value = searchParams.get(key)
      if (value) {
        trackingData[key] = value
        hasAnyParam = true
      }
    }

    if (!hasAnyParam) return
    hasCaptured.current = true

    // Persist to localStorage immediately (survives auth redirects)
    for (const [key, value] of Object.entries(trackingData)) {
      if (value) {
        localStorage.setItem(`totodo_analytics_${key}`, value)
      }
    }

    // Fire-and-forget fingerprint capture
    captureAffiliate(trackingData).catch(() => {})
  }, [searchParams])

  return null
}

/**
 * Top-level app shell.
 *
 * ErrorBoundary wraps everything — catches unhandled React errors.
 * /logout is rendered OUTSIDE DatabaseProvider so Dexie Cloud
 * interactions don't block or interfere with the logout flow.
 * All other routes go through DatabaseProvider → auth gate → app.
 */
export function App() {
  // Patch macOS WKWebView scroll-delta inversion — must run before any DnD Kit rendering
  useEffect(() => {
    patchWindowScrollForMacOS()
  }, [])

  return (
    <ErrorBoundary>
      <OfflineIndicator />
      <BrowserRouter>
        <PreAuthTrackingCapture />
        <PreAuthOAuthConsentCapture />
        <Routes>
          {/* Public routes — outside auth gate */}
          <Route path="/logout" element={<LogoutPage />} />
          <Route path="/login-error" element={<LoginErrorPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/error" element={<ServerErrorPage />} />
          <Route path="/auth/otp-redirect" element={<OtpRedirectPage />} />
          <Route path="/go" element={<GoMarketingPage />} />
          <Route path="/download" element={<DownloadMarketingPage />} />
          <Route path="/affiliate" element={<AffiliateMarketingPage />} />
          <Route path="/support" element={<SupportMarketingPage />} />
          <Route path="/share/:shareId" element={<GuestSharePage />} />

          {/* OAuth consent (MCP apps) — inside the login gate, outside AppLayout. Needs only the
              session token, so it renders as soon as the user is signed in instead of waiting
              for the initial data download. Keep live queries out of this route. */}
          <Route
            path="/oauth/consent"
            element={
              <DatabaseProvider deferInitialSync>
                <CurrentUserProvider>
                  <OAuthConsentPage />
                  <Toaster />
                </CurrentUserProvider>
              </DatabaseProvider>
            }
          />

          {/* All other routes — gated by DatabaseProvider */}
          <Route
            path="*"
            element={
              <DatabaseProvider>
                <CurrentUserProvider>
                  <OAuthConsentResume />
                  <AppLayout>
                    <AppRoutes />
                  </AppLayout>
                  <Suspense fallback={null}>
                    <LazySearchModal />
                    <LazySettingsModal />
                    <LazyWelcomeDialog />
                  </Suspense>
                  <Toaster />
                  <FloatingDoneButton />
                  {import.meta.env.DEV && <DevToolsWidget />}
                </CurrentUserProvider>
              </DatabaseProvider>
            }
          />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  )
}
